import React, { useEffect, useState } from "react";
import {
  FirebaseSession,
  Klass,
  Resource,
  Session,
  TeacherReference,
} from "../types";
import firebase from "firebase";
import { useParams } from "react-router-dom";
import SubHeader from "../components/SubHeader";
import moment from "moment";
import Wrapper from "../components/Layout/Wrapper";
import styled from "styled-components";
import useAuth from "../hooks/useAuth";
import EditComponent from "../components/EditComponent";
import MarkdownIt from "markdown-it";
import MdEditor from "react-markdown-editor-lite";
import "react-markdown-editor-lite/lib/index.css";
import Button from "../components/Button";
import Teacher from "../components/Teacher";
import useTeachers from "../hooks/useTeachers";
import { Input, notification, Select, Switch, Upload } from "antd";
import { DatePicker } from "antd";
import Creatable from "react-select/creatable";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileAlt } from "@fortawesome/free-regular-svg-icons";
import useStudents from "../hooks/useStudents";
import useSessions from "../hooks/useSessions";

const ResourceWrapper = styled.div`
  display: flex;
  cursor: pointer;
  align-items: center;
  h3 {
    margin: 0;
  }
  hgroup {
    margin-left: 16px;
  }
  .icon {
    width: 64px;
    height: 64px;

    background: #fafafa;
    border: 2px solid #efefef;
    box-sizing: border-box;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
  }
`;

const FormGroup = styled.div`
  margin-bottom: 8px;
  label {
    margin-right: 12px;
  }
`;

const { RangePicker } = DatePicker;

const mdParser = new MarkdownIt();

const ContentPageWrapper = styled.div`
  .sub-header {
    margin-bottom: 16px;
  }
  main {
    width: 65%;
    margin-right: 5%;
  }
  aside {
    width: 30%;
  }
`;

const SessionPage = () => {
  const { isAdmin, isSuperUser, accountDetails } = useAuth();
  const { sessionId, classId } = useParams<{
    sessionId: string;
    classId?: string;
  }>();
  const [newTeacher, setNewTeacher] = useState<TeacherReference>({
    teacherId: "",
    role: "Teacher",
  });
  const [activeClass, setActiveClass] = useState<Klass>();
  const [activeSession, setActiveSession] = useState<Session>();
  const [isLoading, setIsLoading] = useState(true);
  const { data: teacherData } = useTeachers();
  const { data: studentData, giveAward } = useStudents();
  const { courses } = useSessions();

  useEffect(() => {
    setIsLoading(true);
    if (!classId) {
      firebase
        .firestore()
        .collection("sessions")
        .doc(sessionId)
        .get()
        .then((snapshot) => {
          const data = snapshot.data() as FirebaseSession;
          if (!data) {
            return undefined;
          }
          return {
            ...data,
            id: snapshot.id,
            startTime: data?.startTime.toMillis
              ? data.startTime.toMillis()
              : data.startTime,
            finishTime: data?.finishTime.toMillis
              ? data.finishTime.toMillis()
              : data.finishTime,
          } as Session;
        })
        .then(setActiveSession)
        .then(() => setIsLoading(false));
    } else {
      firebase
        .firestore()
        .collection("classes")
        .doc(classId)
        .get()
        .then((snapshot) => {
          const data = snapshot.data() as Klass;
          setActiveClass(data);
          const sesh = (data.sessions || []).find(
            (session) => session.id === sessionId
          );
          return sesh;
        })
        .then(setActiveSession)
        .then(() => setIsLoading(false));
    }
  }, [sessionId, classId]);

  const onItemUpdate = async (nextSesh: Partial<Session>) => {
    const nextSession = { ...activeSession, ...nextSesh } as Session;
    if (activeClass) {
      const nextClass = { ...activeClass };
      nextClass.sessions = (nextClass.sessions || []).map((x) =>
        x.id === sessionId ? nextSession : x
      );
      await firebase
        .firestore()
        .collection("classes")
        .doc(classId)
        .update(nextClass);
      setActiveClass(nextClass);
      setActiveSession(nextSession);
      notification.success({ message: "Saved!" });
      return;
    }
    await firebase
      .firestore()
      .collection("sessions")
      .doc(sessionId)
      .update({
        ...nextSession,
      })
      .then(() => {
        notification.success({ message: "Saved!" });
      });
    notification.success({ message: "Saved!" });
    setActiveSession(nextSession);
    return nextSession;
  };

  const onRemoveTeacher = (oldTeacherId?: string) => {
    if (!activeSession) {
      return;
    }
    const nextTeachersArray = (activeSession?.teachers || []).filter(
      (x) => x.teacherId !== oldTeacherId
    );
    const nextSession = {
      ...activeSession,
      teachers: nextTeachersArray,
    };
    onItemUpdate(nextSession);
  };

  const onAddTeacher = (nextTeacher?: TeacherReference) => {
    const nextTeachersArray = [
      ...(activeSession?.teachers || []),
      nextTeacher,
    ] as TeacherReference[];
    onItemUpdate({
      ...activeSession,
      teachers: nextTeachersArray,
    });
  };

  if (isLoading) {
    return <div>Loading</div>;
  }
  if (!activeSession) {
    return <div>This session doesnt exist</div>;
  }
  const unassignedTeachers = (teacherData || []).filter(
    (x) =>
      !(activeSession?.teachers || []).some((t) => t.teacherId === x.teacherId)
  );
  const isSessionOpen =
    moment().isAfter(moment(activeSession.startTime).subtract(30, "minutes")) &&
    moment().isBefore(moment(activeSession.finishTime).add(1, "hour"));
  return (
    <ContentPageWrapper>
      <SubHeader
        title={activeSession?.name}
        subtitle={`${moment(activeSession?.startTime).format("LLLL")}`}
        buttonText={
          isSessionOpen
            ? `Join session`
            : `Opens ${moment(activeSession.startTime)
                .subtract(30, "minutes")
                .format("DD.MM HH:mm")}`
        }
        onClick={() => {
          window.open(activeSession?.videoLink);
        }}
        disabled={!isSessionOpen}
        style={{ filter: `grayscale(${activeSession.isVisible ? "0" : "1"})` }}
      />
      <Wrapper style={{ justifyContent: "space-between" }}>
        <main>
          {isAdmin && (
            <>
              <h3>Admin</h3>
              {classId && (
                <>
                  <FormGroup>
                    <label>Is Active?</label>
                    <Switch
                      checked={activeSession.isVisible}
                      onChange={(next) => {
                        setActiveSession({
                          ...activeSession,
                          isVisible: next,
                        });
                      }}
                    />
                  </FormGroup>
                  <FormGroup>
                    <label>Stars</label>
                    <Input
                      value={activeSession.stars}
                      onChange={(e) =>
                        setActiveSession({
                          ...activeSession,
                          stars: Number(e.currentTarget.value),
                        })
                      }
                    />
                  </FormGroup>
                </>
              )}
              <FormGroup>
                <label>Name</label>
                <Input
                  value={activeSession.name}
                  onChange={(e) =>
                    setActiveSession({
                      ...activeSession,
                      name: e.currentTarget.value,
                    })
                  }
                />
              </FormGroup>
              <FormGroup>
                <label>Link</label>
                <Input
                  value={activeSession.videoLink}
                  onChange={(e) =>
                    setActiveSession({
                      ...activeSession,
                      videoLink: e.currentTarget.value,
                    })
                  }
                />
              </FormGroup>
              {!classId && (
                <FormGroup>
                  <label>Course</label>
                  <Creatable
                    style={{ width: 160 }}
                    isClearable
                    value={{
                      value: activeSession.course || "",
                      label: activeSession.course || "",
                    }}
                    options={courses?.map((x) => ({ value: x, label: x }))}
                    onChange={(course) =>
                      setActiveSession({
                        ...activeSession,
                        course: course?.value,
                      })
                    }
                  />
                </FormGroup>
              )}
              {classId && (
                <RangePicker
                  showTime={{
                    hideDisabledOptions: true,
                  }}
                  defaultValue={[
                    moment(activeSession.startTime),
                    moment(activeSession.finishTime),
                  ]}
                  onCalendarChange={(next) => {
                    if (next?.[0] && next?.[1]) {
                      setActiveSession({
                        ...activeSession,
                        startTime: +next[0],
                        finishTime: +next[1],
                      });
                    }
                  }}
                  format="YYYY-MM-DD HH:mm:ss"
                />
              )}
              <Button
                small
                style={{ marginLeft: 16 }}
                onClick={() => {
                  onItemUpdate({ ...activeSession });
                }}
              >
                Save
              </Button>
            </>
          )}

          <h3>Description</h3>
          <EditComponent
            enableEdit={isAdmin}
            editor={({ onFinish }) => {
              return (
                <>
                  <MdEditor
                    value={activeSession.description}
                    style={{ height: "500px" }}
                    renderHTML={(text) => mdParser.render(text)}
                    onChange={(next) =>
                      setActiveSession({
                        ...activeSession,
                        description: next.text,
                      })
                    }
                  />
                  <Button
                    small
                    style={{ marginTop: 16 }}
                    onClick={() => {
                      onItemUpdate({ description: activeSession.description });
                      onFinish(activeSession.description);
                    }}
                  >
                    Update
                  </Button>
                </>
              );
            }}
          >
            <div
              dangerouslySetInnerHTML={{
                __html: mdParser.render(activeSession.description),
              }}
            />
          </EditComponent>
          {isAdmin && (activeSession?.resources || []).length > 0 && (
            <>
              <h3>Resources</h3>
              {activeSession.resources.map((resource) => (
                <ResourceWrapper
                  onClick={() => {
                    firebase
                      .storage()
                      .ref(resource.path)
                      .getDownloadURL()
                      .then((url) => {
                        window.open(url);
                      });
                  }}
                >
                  <div className="icon">
                    <FontAwesomeIcon icon={faFileAlt} size="2x" />
                  </div>
                  <hgroup>
                    <h3>{resource.name}</h3>
                    {resource.type}
                  </hgroup>
                </ResourceWrapper>
              ))}
            </>
          )}
          {isAdmin && (
            <div style={{ marginTop: 16 }}>
              <Upload
                name="resource"
                customRequest={async ({ file, onSuccess }) => {
                  const imagePath = `session/${sessionId}/${file.name}`;
                  const arrayBuffer = await file.arrayBuffer();
                  const nextResources = [
                    ...(activeSession?.resources || []),
                    {
                      name: file.name,
                      path: imagePath,
                      type: file.type,
                    },
                  ] as Resource[];
                  firebase.storage().ref(imagePath).put(arrayBuffer);
                  onItemUpdate({
                    resources: nextResources,
                  });

                  onSuccess({ msg: "ok" }, file);
                }}
                fileList={[]}
              >
                <Button small>Upload resources</Button>
              </Upload>
            </div>
          )}
        </main>
        <aside>
          <h3>Teachers</h3>
          {(activeClass?.teachers || []).map((teacher) => {
            const teacherDetail = teacherData?.find(
              (x) => x.teacherId === teacher.teacherId
            );
            return <Teacher teacher={teacherDetail} subtitle={teacher.role} />;
          })}
          {(activeSession?.teachers || []).map((teacher) => {
            const teacherDetail = teacherData?.find(
              (x) => x.teacherId === teacher.teacherId
            );
            return (
              <Teacher
                teacher={teacherDetail}
                subtitle={teacher.role}
                onRemove={isAdmin ? onRemoveTeacher : undefined}
              />
            );
          })}
          {isSuperUser && unassignedTeachers.length > 0 && (
            <div style={{ marginTop: 32 }}>
              <Select
                onChange={(next) =>
                  setNewTeacher({
                    role: newTeacher?.role,
                    teacherId: next as string,
                  })
                }
                value={newTeacher.teacherId}
              >
                <Select.Option value="">-- Select a teacher --</Select.Option>
                {unassignedTeachers.map((x) => (
                  <Select.Option value={x.teacherId}>{x.name}</Select.Option>
                ))}
              </Select>
              <br />
              <Select
                onChange={(next) =>
                  setNewTeacher({
                    teacherId: newTeacher?.teacherId,
                    role: next as string,
                  })
                }
                value={newTeacher.role}
              >
                <Select.Option value="">-- Select a role --</Select.Option>
                {["Teacher", "Assistant"].map((x) => (
                  <Select.Option value={x}>{x}</Select.Option>
                ))}
              </Select>
              <br />
              <Button small onClick={() => onAddTeacher(newTeacher)}>
                Add
              </Button>
            </div>
          )}
          {isAdmin && classId && (
            <>
              <h3>Attendence</h3>
              {activeClass?.students?.map((student) => (
                <Teacher
                  teacher={studentData?.find((x) => x.id === student.id)}
                  subtitle={
                    activeSession.attendants?.includes(student.id)
                      ? "Attended"
                      : "Unmarked"
                  }
                  type="add"
                  onRemove={
                    activeSession.attendants?.includes(student.id)
                      ? undefined
                      : () => {
                          onItemUpdate({
                            attendants: [
                              ...(activeSession.attendants || []),
                              student.id,
                            ],
                          });
                          giveAward(
                            {
                              description: `Thanks for attending the session ${activeSession.name}`,
                              stars: activeSession.stars,
                              teacher:
                                accountDetails?.name || accountDetails?.uid,
                            },
                            student.id
                          );
                        }
                  }
                />
              ))}
            </>
          )}
        </aside>
      </Wrapper>
    </ContentPageWrapper>
  );
};

export default SessionPage;
